/*
 * Lemonade Training Oy copy rights all right reserved.
 * Membership Request Type Definitions (F016 — commitment memberships)
 *
 * Root collection `MembershipRequests`. One shape serves actionable
 * early-exit requests (goodwill cancel now; pause is a planned future use of
 * the same shape) and awareness notices (statutory cancellation, dunning).
 */

import { Timestamp } from "firebase/firestore";

/**
 * Runtime enumerations are the single source of truth — the unions derive
 * from them, so the type guard below can never drift from the type.
 */
export const MEMBERSHIP_REQUEST_TYPES = [
  "cancel_goodwill",
  "cancel_statutory",
  "pause",
] as const;
export const MEMBERSHIP_REQUEST_CATEGORIES = ["decision", "awareness"] as const;
export const MEMBERSHIP_REQUEST_STATUSES = [
  "pending",
  "approved",
  "denied",
  "withdrawn",
  "effective",
  "contested",
  "upheld",
  "reinstated",
] as const;

/**
 * "cancel_statutory" is an illness/pregnancy termination the member exercises
 * by declaration — the gym is told, and may dispute it afterwards, but cannot
 * gate it. "pause" is reserved for the planned pause feature — shape shared now.
 */
export type MembershipRequestType = (typeof MEMBERSHIP_REQUEST_TYPES)[number];

/**
 * decision = needs owner action (goodwill request);
 * awareness = FYI only (statutory cancellation, dunning notices).
 */
export type MembershipRequestCategory =
  (typeof MEMBERSHIP_REQUEST_CATEGORIES)[number];

/**
 * Two lifecycles share this union. A goodwill request the gym answers:
 * pending → approved | denied | withdrawn. A statutory termination, which
 * takes effect by declaration and so is never "approved":
 * effective → contested → upheld | reinstated.
 */
export type MembershipRequestStatus =
  (typeof MEMBERSHIP_REQUEST_STATUSES)[number];

/**
 * Owner-chosen refund treatment for upfront payers, SERVER-COMPUTED at
 * approve — never client-written (the client must not be able to influence
 * money movement).
 */
export type RequestSettlementTreatment = "pro_rata" | "credit" | "end_access";

export type RequestSettlement = {
  treatment: RequestSettlementTreatment;
  /** € in cents, server-computed from the server-side membership (never client). */
  amountCents: number;
};

/**
 * Non-health statutory metadata for a statutory (self-attested) cancellation.
 * The medical document is NEVER received or stored (GDPR Article 9 data
 * minimization) — only the verified flag, the category, and the effective
 * date persist on the awareness request.
 */
export type StatutoryGroundCategory = "illness" | "pregnancy";

/**
 * How a gym's statutory cancellations behave. Absent means
 * "effective_on_submit" — a gym must opt OUT of the strictest regime, never
 * accidentally opt out of it, because the strict one is what jurisdictions
 * granting an unconditional termination right require.
 */
export const STATUTORY_CANCEL_MODES = [
  "effective_on_submit",
  "owner_review",
] as const;

export type StatutoryCancelMode = (typeof STATUTORY_CANCEL_MODES)[number];

export type CancelLane = "default" | "statutory" | "goodwill";

type CancelRequestBase = {
  subscriptionId: string;
  membershipProductUID: string;
};

/** The submitMembershipCancel callable's request. */
export type SubmitMembershipCancelRequest =
  | (CancelRequestBase & { lane: "default" })
  | (CancelRequestBase & { lane: "goodwill"; reason?: string })
  | (CancelRequestBase & {
      lane: "statutory";
      statutoryGroundCategory: StatutoryGroundCategory;
      declarationAccepted: true;
      /**
       * 12:00 gym time on the picked gym date (statutoryEndInstant); mobile
       * sends epoch ms, web an ISO string. Absent means "now".
       */
      effectiveDate?: number | string;
    });

export type MembershipRequest = {
  id: string;
  boxID: string;
  userUID: string;
  membershipProductUID: string;
  subscriptionId: string;
  requestType: MembershipRequestType;
  category: MembershipRequestCategory;
  status: MembershipRequestStatus;
  /**
   * Member free-text (goodwill requests). Owner-readable → length-capped and
   * disclosed to the member; guidance steers away from medical detail.
   * Absent on awareness items.
   */
  reason?: string;
  /**
   * Present only after approve (server-computed). MUST be absent at create —
   * Firestore rules pin status to "pending" and reject any pre-written
   * settlement/resolvedBy/resolvedAt on create (forgery guard); the
   * optional-ness here is the type mirror of that rule.
   */
  settlement?: RequestSettlement;
  /** Owner note on resolve. */
  decisionNote?: string;

  // Statutory-cancellation awareness metadata (no document, no diagnosis):
  statutoryGroundVerified?: boolean;
  statutoryGroundCategory?: StatutoryGroundCategory;
  terminationEffectiveAt?: Timestamp | Date;
  /**
   * Which declaration wording the member affirmed, e.g. "2026-08-10.1". The
   * version only — never the text, never anything health-related. Without it
   * the declaration is unprovable if the gym later disputes the ground.
   */
  declarationWordingVersion?: string;
  /**
   * Which regime governed this item when it was raised. Gyms may be
   * configured so statutory cancellations take effect on submit (the strict
   * default) or route to the owner first, so the queue must know which one
   * applied rather than inferring it from today's setting.
   */
  statutoryMode?: StatutoryCancelMode;

  // The gym disputing a statutory termination. It cannot undo one — the
  // termination took effect by declaration — but an untrue ground makes it
  // void, so the dispute is recorded and settled afterwards. Evidence is
  // exchanged directly between gym and member; nothing medical is ever
  // received here.
  contestedAt?: Timestamp | Date;
  /**
   * When the gym expects to have seen proof. Displayed to both sides, and
   * advisory only — no scheduler reads it, so a window passing changes
   * nothing on its own. Closing a dispute is always a deliberate act.
   */
  evidenceWindowEndsAt?: Timestamp | Date;
  /**
   * The gym's note to the member on contesting. Member-readable, so it is
   * length-capped and must never ask for medical detail.
   */
  contestNote?: string;

  createdAt: Timestamp | Date;
  resolvedAt?: Timestamp | Date;
  resolvedBy?: string;
};

/**
 * Owner-readable free-text cap (limits accidental sensitive-data exposure in
 * a field another person reads). The value is the contract; Firestore rules
 * and the client both enforce it.
 */
export const MEMBERSHIP_REQUEST_REASON_MAX_LENGTH = 500;

/**
 * Shape guard only — verifies required fields exist with valid values so a
 * Firestore read can be narrowed safely. It is NOT a create-payload
 * validator: it does not reject settlement/resolvedBy/resolvedAt present at
 * create (Firestore rules own that forgery guard) and does not validate
 * optional-field types.
 */
export function isMembershipRequest(o: unknown): o is MembershipRequest {
  if (!o || typeof o !== "object") {
    return false;
  }
  const r = o as MembershipRequest;
  const isTimestampLike =
    r.createdAt instanceof Date ||
    (!!r.createdAt &&
      typeof r.createdAt === "object" &&
      typeof (r.createdAt as { toDate?: unknown }).toDate === "function");
  return (
    typeof r.id === "string" &&
    typeof r.boxID === "string" &&
    typeof r.userUID === "string" &&
    typeof r.membershipProductUID === "string" &&
    typeof r.subscriptionId === "string" &&
    (MEMBERSHIP_REQUEST_TYPES as readonly string[]).includes(r.requestType) &&
    (MEMBERSHIP_REQUEST_CATEGORIES as readonly string[]).includes(r.category) &&
    (MEMBERSHIP_REQUEST_STATUSES as readonly string[]).includes(r.status) &&
    isTimestampLike
  );
}
